import React, { useState, useEffect, useMemo } from 'react';
import { PageContainer, PageToolbar, ToolbarDivider } from '../../components/ui/PageLayout';
import {
  AnimatedTabIndicator,
  animatedTabTriggerClass,
} from '../../components/ui/AnimatedTabIndicator';
import toast from '../../services/toast';
import AddMemberModal from '../Dashboard/AddMemberModal';
import MemberGitHubSuggestModal from './MemberGitHubSuggestModal';
import LinkGitHubModal from './LinkGitHubModal';
import MemberImportModal from './MemberImportModal';
import MembersListTab from './MembersListTab';
import MembersInvitesTab from './MembersInvitesTab';
import MembersRequestsTab from './MembersRequestsTab';
import MembersFilterModal from './MembersFilterModal';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { UserPlus } from 'lucide-react';
import { PageToolbarSkeleton, TableSkeleton } from '../../components/ui/Skeleton';
import { useOrganisationName } from '../../hooks/useOrganisationName';
import { useResolvedOrgId } from '../../hooks/useResolvedOrgId';
import { useMembersPageQueries } from './useMembersPageQueries';
import { useMembersFilterState, useMembersDataTable } from './useMembersTableState';
import { buildMembersTableColumns } from './membersTableColumns';

const Members = () => {
  const orgName = useOrganisationName();
  const { orgId, ready } = useResolvedOrgId();
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [githubSuggestOpen, setGithubSuggestOpen] = useState(false);
  const [linkGitHubMember, setLinkGitHubMember] = useState<any>(null);
  const [importOpen, setImportOpen] = useState(false);
  const canManageMembers = useFeatureFlag('api.organisations.members.write', false);
  const canManageInvites = useFeatureFlag('api.organisations.invites.write', false);
  const [deletingInviteId, setDeletingInviteId] = useState<any>(null);
  const [grantingRequestId, setGrantingRequestId] = useState<any>(null);

  const filterState = useMembersFilterState(canManageInvites);

  const {
    members,
    invites,
    requests,
    streamOptions,
    orgRepositories,
    projectOptions,
    loading,
    invalidateMembers,
    deleteInviteMutation,
    grantRequestMutation,
    membersQueryError,
  } = useMembersPageQueries({
    orgId,
    ready,
    debouncedSearch: filterState.debouncedSearch,
    memberFilter: filterState.memberFilter,
    canManageInvites,
    filterModalOpen: filterState.filterModalOpen,
    draftFilter: filterState.draftFilter,
  });

  const columns = useMemo(
    () =>
      buildMembersTableColumns({
        canManageMembers,
        onLinkGitHubMember: setLinkGitHubMember,
      }),
    [canManageMembers]
  );

  const { table, pageInput, setPageInput, goToMember, handleRowActivate } = useMembersDataTable({
    members,
    columns,
    debouncedSearch: filterState.debouncedSearch,
    memberFilter: filterState.memberFilter,
  });

  useEffect(() => {
    if (membersQueryError) {
      toast.error(membersQueryError.message || 'Failed to load members');
    }
  }, [membersQueryError]);

  const handleGrantRequest = async (requestId, email) => {
    if (!orgId) return;
    setGrantingRequestId(requestId);
    try {
      await grantRequestMutation.mutateAsync({ requestId });
      toast.success(`Invite sent to ${email}`);
    } finally {
      setGrantingRequestId(null);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!orgId) return;
    setDeletingInviteId(inviteId);
    try {
      await deleteInviteMutation.mutateAsync(inviteId);
    } finally {
      setDeletingInviteId(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageToolbarSkeleton />
        <div className="border-t border-dark-border my-4" />
        <section>
          <TableSkeleton cols={6} rows={8} />
        </section>
      </PageContainer>
    );
  }

  if (!orgId) {
    return (
      <PageContainer>
        <PageToolbar title="Members" />
        <p className="text-slate-400">No organisation found. Please complete setup.</p>
      </PageContainer>
    );
  }

  const orgLabel = orgName || 'Organisation';

  return (
    <PageContainer>
      <PageToolbar
        title="Members"
        breadcrumbItems={[{ label: orgLabel, to: '/' }, { label: 'Members' }]}
      >
        <button
          type="button"
          onClick={() => setAddMemberOpen(true)}
          className="btn-primary-mdl inline-flex items-center gap-2"
          aria-label="Invite member"
          title="Invite member"
        >
          <UserPlus className="w-4 h-4" />
          Invite
        </button>
      </PageToolbar>

      <div className="border-b border-dark-border">
        <nav
          className="flex gap-1 -mb-px overflow-x-auto"
          role="tablist"
          aria-label="Members sections"
          onKeyDown={filterState.onMembersTabListKeyDown}
        >
          <button
            type="button"
            role="tab"
            id="members-tab-active"
            aria-selected={filterState.activeMembersTab === 'active'}
            aria-controls="members-tabpanel-active"
            tabIndex={filterState.activeMembersTab === 'active' ? 0 : -1}
            onClick={() => filterState.setActiveMembersTab('active')}
            className={animatedTabTriggerClass(filterState.activeMembersTab === 'active')}
          >
            <AnimatedTabIndicator
              layoutId="members-tab-indicator"
              isActive={filterState.activeMembersTab === 'active'}
            />
            <span className="relative z-10 inline-flex items-center gap-2">
              Active members
              <span className="rounded-full bg-dark-elevated px-2 py-0.5 text-xs font-semibold text-slate-300">
                {members.length}
              </span>
            </span>
          </button>
          <button
            type="button"
            role="tab"
            id="members-tab-invites"
            aria-selected={filterState.activeMembersTab === 'invites'}
            aria-controls="members-tabpanel-invites"
            tabIndex={filterState.activeMembersTab === 'invites' ? 0 : -1}
            onClick={() => filterState.setActiveMembersTab('invites')}
            className={animatedTabTriggerClass(filterState.activeMembersTab === 'invites')}
          >
            <AnimatedTabIndicator
              layoutId="members-tab-indicator"
              isActive={filterState.activeMembersTab === 'invites'}
            />
            <span className="relative z-10 inline-flex items-center gap-2">
              Pending invites
              {invites.length > 0 && (
                <span className="rounded-full bg-amber-900/30 px-2 py-0.5 text-amber-300 font-semibold">
                  {invites.length}
                </span>
              )}
            </span>
          </button>
          {canManageInvites ? (
            <button
              type="button"
              role="tab"
              id="members-tab-requests"
              aria-selected={filterState.activeMembersTab === 'requests'}
              aria-controls="members-tabpanel-requests"
              tabIndex={filterState.activeMembersTab === 'requests' ? 0 : -1}
              onClick={() => filterState.setActiveMembersTab('requests')}
              className={animatedTabTriggerClass(filterState.activeMembersTab === 'requests')}
            >
              <AnimatedTabIndicator
                layoutId="members-tab-indicator"
                isActive={filterState.activeMembersTab === 'requests'}
              />
              <span className="relative z-10 inline-flex items-center gap-2">
                Requests
                {requests.length > 0 && (
                  <span className="rounded-full bg-brand-purple/20 px-2 py-0.5 text-xs font-semibold text-brand-purple-light">
                    {requests.length}
                  </span>
                )}
              </span>
            </button>
          ) : null}
        </nav>
      </div>
      <ToolbarDivider />

      <div className="space-y-6">
        {filterState.activeMembersTab === 'active' && (
          <MembersListTab
            members={members}
            hasListFilters={filterState.hasListFilters}
            search={filterState.search}
            setSearch={filterState.setSearch}
            memberFilter={filterState.memberFilter}
            setMemberFilter={filterState.setMemberFilter}
            setDraftFilter={filterState.setDraftFilter}
            setFilterModalOpen={filterState.setFilterModalOpen}
            hasActiveScopeFilter={filterState.hasActiveScopeFilter}
            canManageMembers={canManageMembers}
            moreMenuRef={filterState.moreMenuRef}
            moreMenuOpen={filterState.moreMenuOpen}
            setMoreMenuOpen={filterState.setMoreMenuOpen}
            setImportOpen={setImportOpen}
            setGithubSuggestOpen={setGithubSuggestOpen}
            setAddMemberOpen={setAddMemberOpen}
            table={table}
            pageInput={pageInput}
            setPageInput={setPageInput}
            goToMember={goToMember}
            handleRowActivate={handleRowActivate}
          />
        )}

        {filterState.activeMembersTab === 'invites' && (
          <MembersInvitesTab
            invites={invites}
            deletingInviteId={deletingInviteId}
            handleDeleteInvite={handleDeleteInvite}
            setAddMemberOpen={setAddMemberOpen}
          />
        )}

        {filterState.activeMembersTab === 'requests' && canManageInvites && (
          <MembersRequestsTab
            requests={requests}
            grantingRequestId={grantingRequestId}
            handleGrantRequest={handleGrantRequest}
          />
        )}
      </div>

      <MembersFilterModal
        isOpen={filterState.filterModalOpen}
        onClose={filterState.closeFilterModal}
        draftFilter={filterState.draftFilter}
        setDraftFilter={filterState.setDraftFilter}
        streamOptions={streamOptions}
        projectOptions={projectOptions}
        orgRepositories={orgRepositories}
        onApply={filterState.applyFilter}
      />

      <AddMemberModal
        isOpen={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        orgId={orgId}
        onSuccess={invalidateMembers}
      />
      <MemberGitHubSuggestModal
        isOpen={githubSuggestOpen}
        onClose={() => setGithubSuggestOpen(false)}
        orgId={orgId}
        onLinked={invalidateMembers}
      />
      <LinkGitHubModal
        isOpen={linkGitHubMember !== null}
        onClose={() => setLinkGitHubMember(null)}
        orgId={orgId}
        member={linkGitHubMember}
        onSuccess={() => {
          setLinkGitHubMember(null);
          invalidateMembers();
        }}
      />
      <MemberImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        orgId={orgId}
        onSuccess={invalidateMembers}
      />
    </PageContainer>
  );
};

export default Members;
